import { TreehouseRedemptionQueueAbi } from '../abis/treehouse'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * Treehouse tAssets (tETH / tAVAX / tHYPE) — `TreehouseRedemptionV2`,
 * fully enumerable on-chain in one multicall: `getPendingRedeems(user)`
 * plus the live `waitingPeriod()`.
 *
 * Three things to get right:
 *
 * 1. **`requestId` is the ARRAY POSITION.** `finalizeRedeem(_redeemIndex)`
 *    indexes `redemptionInfo[user]`, which is maintained with swap-and-pop
 *    (`_deleteRedeemEntry` moves the last element into the freed slot), so
 *    finalizing slot 0 renumbers the rest. Never cache an id — re-read this
 *    list immediately before building a claim. Same hazard as Binance wBETH.
 *
 * 2. **`amountUnderlying` is a CEILING, not the payout.** The struct's
 *    `assets` is `previewRedeem(shares)` at request time; settlement uses
 *    the MINIMUM of the tAsset rate at request and at finalize, scaled by
 *    the underlying's own ratio move, and then deducts `redemptionFee`
 *    (5 bps). The contract explicitly reverts if the computed return
 *    exceeds `assets`, so this number can only be met or missed.
 *
 * 3. **Time alone does not guarantee a claim.** `finalizeRedeem` also
 *    requires the Vault to still hold the underlying
 *    (`InsufficientFundsInVault`) and the shared `RedemptionController` not
 *    to be paused — global conditions we cannot attribute per user, so a
 *    request can read `claimable` here and still revert.
 *
 * The waiting period is read rather than assumed: it is an owner dial and
 * it already differs per deployment (7 d Ethereum, 17 d Avalanche, 10 d
 * HyperEVM) against docs that describe 7 days everywhere.
 */
export const readerTreehouseRedemption: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const res = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getPendingRedeems',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'waitingPeriod',
          params: [],
        },
      ] as unknown as never[],
      abi: [
        TreehouseRedemptionQueueAbi,
        TreehouseRedemptionQueueAbi,
      ] as unknown as never,
    })) as unknown[]

    const reqs = res[0] as Array<Record<string, unknown>> | undefined
    const waitingPeriod = toNumber(res[1])
    if (!Array.isArray(reqs) || waitingPeriod === undefined) return []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < reqs.length; i++) {
      const r = reqs[i]
      const startTime = toNumber(r.startTime)
      const assets = toBigInt(r.assets)
      const shares = toBigInt(r.shares)
      if (startTime === undefined || assets === undefined) continue

      const readyAt = startTime + waitingPeriod
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        // Positional — see note 1 above.
        requestId: String(i),
        amountUnderlying: assets.toString(),
        shares: shares?.toString(),
        status: computeStatus(readyAt),
        readyAt,
      })
    }
    return out
  },
}
